import React, { useEffect, useState } from 'react';
import { StyleSheet, View, ActivityIndicator, Platform } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import { useAuthStore } from '@/store';
import { authApi } from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';
import StatusContainer from '@/components/StatusContainer';

// Admin impersonation handoff screen (web/desktop only).
//
// The session-based admin dashboard POSTs to /admin/users/:id/impersonate,
// which mints a one-time code and redirects here: /impersonate?code=...
// This screen redeems the code for a full target-user session, stashes the
// admin's current SPA session (if any), and drops into the app. A persistent
// banner with a Stop button is rendered by _layout.web.jsx for the duration.
export default function ImpersonateScreen() {
  const { colors } = useTheme();
  const params = useLocalSearchParams();
  const code = params?.code ? String(params.code) : '';

  const [status, setStatus] = useState('loading'); // 'loading' | 'error'
  const [message, setMessage] = useState('');

  const startImpersonation = useAuthStore(state => state.startImpersonation);

  useEffect(() => {
    // Desktop-only feature. On native (mobile apps), bounce to the app — the
    // admin tooling lives in the web dashboard.
    if (Platform.OS !== 'web') {
      router.replace('/app');
      return;
    }

    let cancelled = false;

    const redeem = async () => {
      if (!code) {
        setStatus('error');
        setMessage('Missing impersonation code.');
        return;
      }

      try {
        const result = await authApi.impersonateExchange(code);
        if (cancelled) return;

        startImpersonation(
          result.tokens,
          result.user,
          { returnUrl: result.returnUrl }
        );

        // Hand off into the app shell. replace (not push) so the handoff
        // screen isn't in the back-stack.
        router.replace('/app');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setMessage(err?.message || 'This impersonation link is invalid or has expired.');
      }
    };

    redeem();

    return () => { cancelled = true; };
  }, [code, startImpersonation]);

  if (status === 'error') {
    return (
      <View style={[styles.container, { backgroundColor: colors.solid_color_primary }]}>
        <MetaHead title="Impersonation — Gab AI" />
        <View style={styles.statusWrap}>
          <StatusContainer
            type="error"
            title="Impersonation failed"
            message={message}
            actionText="Go home"
            onAction={() => router.replace('/')}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.solid_color_primary }]}>
      <MetaHead title="Starting impersonation — Gab AI" />
      <ActivityIndicator size="large" color={colors.accent_color_primary} />
      <Text size="n" color="secondary">
        Starting impersonation…
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 24,
  },
  statusWrap: {
    width: '100%',
    maxWidth: 480,
  },
});
